import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, Menu, X, LogOut, LayoutGrid } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/contact", label: "Contact" },
  { to: "/affiliate", label: "Affiliate" },
];

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-background/70 backdrop-blur-xl border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="group flex items-baseline gap-2" data-testid="nav-logo">
          <span className="font-display text-2xl md:text-3xl leading-none tracking-tight">Coastal</span>
          <span className="label text-accent">Chemistry</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="label text-foreground/70 hover:text-foreground transition-colors duration-300"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          {user && user.role === "admin" && (
            <Link to="/admin" className="hidden md:inline-flex label text-accent hover:opacity-70 transition-opacity" data-testid="nav-admin">
              <LayoutGrid size={16} strokeWidth={1.5} className="mr-1" /> Admin
            </Link>
          )}
          {user && user.is_affiliate && (
            <Link to="/affiliate-dashboard" className="hidden md:inline-flex label text-foreground/70 hover:text-foreground transition-colors" data-testid="nav-affiliate-dashboard">
              Affiliate Hub
            </Link>
          )}
          {user ? (
            <div className="hidden md:flex items-center gap-4">
              <Link to="/account" className="text-foreground/70 hover:text-foreground transition-colors" data-testid="nav-account">
                <User size={20} strokeWidth={1.5} />
              </Link>
              <button onClick={handleLogout} className="text-foreground/70 hover:text-foreground transition-colors" data-testid="nav-logout">
                <LogOut size={20} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden md:inline-flex label text-foreground/70 hover:text-foreground transition-colors" data-testid="nav-login">
              Account
            </Link>
          )}

          <Link to="/cart" className="relative text-foreground hover:text-accent transition-colors" data-testid="nav-cart">
            <ShoppingBag size={22} strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 h-4 min-w-4 px-1 flex items-center justify-center bg-primary text-primary-foreground text-[10px] font-mono" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          <button className="md:hidden text-foreground" onClick={() => setOpen((v) => !v)} data-testid="nav-menu-toggle">
            {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-background border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="label text-foreground" data-testid={`mobile-link-${l.label.toLowerCase()}`}>
                  {l.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/account" className="label text-foreground" data-testid="mobile-account">Account</Link>
                  {user.is_affiliate && <Link to="/affiliate-dashboard" className="label text-foreground" data-testid="mobile-affiliate-dashboard">Affiliate Hub</Link>}
                  {user.role === "admin" && <Link to="/admin" className="label text-accent" data-testid="mobile-admin">Admin</Link>}
                  <button onClick={handleLogout} className="label text-left text-foreground" data-testid="mobile-logout">Log out</button>
                </>
              ) : (
                <Link to="/login" className="label text-foreground" data-testid="mobile-login">Account</Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
